import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft,
  User,
  Calendar,
  Clock,
  Target,
  Flag,
  Edit,
  MessageCircle,
  CheckSquare,
  FileText,
  Plus,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import EditTaskModal from '../../components/EditTaskModal';

interface Task {
  _id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  progress: number;
  dueDate: string;
  estimatedHours: number;
  actualHours: number;
  project: {
    _id: string;
    title: string;
  };
  assignedTo: {
    _id: string;
    firstName: string;
    lastName: string;
  }[];

  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  comments: {
    _id: string;
    user: {
      _id: string;
      firstName: string;
      lastName: string;
    };
    content: string;
    createdAt: string;
  }[];
  timeEntries: {
    _id: string;
    user: {
      _id: string;
      firstName: string;
      lastName: string;
    };
    hours: number;
    description: string;
    date: string;
  }[];
  createdAt: string;
}

// Get the correct API base URL based on environment
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

const TaskDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [newComment, setNewComment] = useState('');
  const [newTimeEntry, setNewTimeEntry] = useState({
    hours: 0,
    description: '',
  });
  const [availableUsers, setAvailableUsers] = useState<
    { _id: string; firstName: string; lastName: string }[]
  >([]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const API_BASE_URL = getApiBaseUrl();

  const isAssignedDeveloper =
    user?.role === 'developer' &&
    task?.assignedTo?.some((dev) => dev._id === user._id);

  const showResponseButtons = isAssignedDeveloper && task?.status === 'todo';

  useEffect(() => {
    if (id) {
      fetchTask();
      fetchAvailableUsers();
    }
  }, [id]);

  const fetchAvailableUsers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE_URL}/users?role=developer`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setAvailableUsers(data.users || []);
      }
    } catch (err) {
    }
  };

  const fetchTask = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/tasks/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load task');
      }

      const data = await response.json();
      setTask(data.task);
    } catch (error) {
      toast.error('Unable to load task.');
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (action: 'accept' | 'decline') => {
    try {
      const res = await fetch(`${API_BASE_URL}/tasks/${task?._id}/respond`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ action }),
      });

      if (!res.ok) throw new Error('Failed to respond to task');

      const data = await res.json();
      setTask(data.task); // update state
      toast.success(`Task ${action}ed successfully`);
    } catch (err) {
      let errorMessage = 'Error updating task';
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const res = await fetch(`${API_BASE_URL}/tasks/${task?._id}/comment`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ content: newComment }),
      });

      if (!res.ok) throw new Error('Failed to add comment');
      const data = await res.json();

      setTask((prev) => (prev ? { ...prev, comments: data.comments } : prev));
      setNewComment('');
      toast.success('Comment added');
    } catch (err) {
      toast.error('Failed to add comment');
    }
  };

  const handleLogTime = async () => {
    const { hours, description } = newTimeEntry;

    if (!hours || !description.trim()) return;

    try {
      const res = await fetch(`${API_BASE_URL}/tasks/${task?._id}/time`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ hours, description }),
      });

      if (!res.ok) throw new Error('Failed to log time');

      const data = await res.json();
      setTask((prev) =>
        prev
          ? {
              ...prev,
              timeEntries: data.timeEntries,
              actualHours: data.actualHours,
            }
          : prev
      );
      setNewTimeEntry({ hours: 0, description: '' });
      toast.success('Time logged');
    } catch (err) {
      toast.error('Failed to log time');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'todo':
        return 'text-gray-600 bg-gray-100';
      case 'in-progress':
        return 'text-blue-600 bg-blue-100';
      case 'review':
        return 'text-purple-600 bg-purple-100';
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-100';
      case 'high':
        return 'text-orange-600 bg-orange-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getTimeUntilDue = (dueDate: string) => {
    const days = Math.ceil(
      (new Date(dueDate).getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24)
    );
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    return `Due in ${days} days`;
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-16 bg-gray-200 rounded-lg animate-pulse"></div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Task not found
        </h3>
        <p className="text-gray-600 mb-6">
          The task you're looking for doesn't exist or you don't have access to
          it.
        </p>
        <Link
          to="/dashboard/tasks"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tasks
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditTaskModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        task={{
          ...task,
          assignedTo: task.assignedTo.map((dev) => dev._id), // 🔥 FIXED HERE
        }}
        availableUsers={availableUsers} // or pass fetched users
        onSave={async (updatedTask) => {
          try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/tasks/${task?._id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(updatedTask),
            });

            if (!res.ok) throw new Error('Failed to update task');
            const data = await res.json();
            setTask(data.task);
            toast.success('Task updated');
          } catch (err) {
            toast.error('Failed to update task');
          } finally {
            setIsEditModalOpen(false);
          }
        }}
      />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/tasks"
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{task.title}</h1>
            <p className="text-gray-600 mt-1">Task Details</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </button>
        </div>
      </div>

      {/* Task Overview */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <span
                  className={`px-3 py-1 text-sm font-medium rounded-full capitalize ${getStatusColor(
                    task.status
                  )}`}
                >
                  {task.status.replace('-', ' ')}
                </span>
                <span
                  className={`px-3 py-1 text-sm font-medium rounded-full capitalize ${getPriorityColor(
                    task.priority
                  )}`}
                >
                  {task.priority} priority
                </span>
                {showResponseButtons && (
                  <div className="task-response-buttons">
                    <button
                      onClick={() => handleRespond('accept')}
                      className="btn-accept"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => handleRespond('decline')}
                      className="btn-decline"
                    >
                      Decline
                    </button>
                  </div>
                )}
              </div>

              <p className="text-gray-700 mb-6">{task.description}</p>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                    Progress
                  </h3>
                  <div className="flex items-center space-x-3">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={task.progress}
                      onChange={async (e) => {
                        const newProgress = parseInt(e.target.value);
                        setTask((prev) =>
                          prev ? { ...prev, progress: newProgress } : prev
                        );
                        try {
                          const token = localStorage.getItem('accessToken');
                          const res = await fetch(
                            `${API_BASE_URL}/tasks/${task._id}`,
                            {
                              method: 'PUT',
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                              },
                              body: JSON.stringify({ progress: newProgress }),
                            }
                          );
                          if (!res.ok)
                            throw new Error('Failed to update progress');
                          toast.success('Progress updated');
                        } catch (err) {
                          toast.error('Failed to update progress');
                        }
                      }}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium text-gray-900">
                      {task.progress}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Task Details
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center text-sm">
                    <Target className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-gray-600">Project:</span>
                    <Link
                      to={`/dashboard/projects/${task.project._id}`}
                      className="ml-auto font-medium text-blue-600 hover:text-blue-700"
                    >
                      {task.project.title}
                    </Link>
                  </div>

                  {task.dueDate && (
                    <div className="flex items-center text-sm">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">Due:</span>
                      <span className="ml-auto font-medium">
                        {getTimeUntilDue(task.dueDate)}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center text-sm">
                    <Clock className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-gray-600">Time:</span>
                    <span className="ml-auto font-medium">
                      {task.actualHours}h / {task.estimatedHours}h
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Assigned To
                </h3>
                <div className="space-y-2">
                  {task.assignedTo.length > 0 ? (
                    task.assignedTo.map((assignee) => (
                      <div
                        key={assignee._id}
                        className="flex items-center space-x-3"
                      >
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {assignee.firstName?.[0] ?? ''}
                            {assignee.lastName?.[0] ?? ''}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">
                            {assignee.firstName} {assignee.lastName}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-600">No one assigned</p>
                  )}
                </div>
                {task.assignedTo.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Assignment Responses
                    </h4>
                    <ul className="space-y-1 text-sm">
                      {task.assignedTo.map((dev) => {
                        const response =
                          (task as any).assignmentResponses?.[dev._id] ||
                          'pending';

                        return (
                          <li
                            key={dev._id}
                            className="flex justify-between items-center"
                          >
                            <span>
                              {dev.firstName} {dev.lastName}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium ${
                                response === 'accept'
                                  ? 'bg-green-100 text-green-700'
                                  : response === 'decline'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {response}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', name: 'Overview', icon: FileText },
              { id: 'comments', name: 'Comments', icon: MessageCircle },
              { id: 'time', name: 'Time Tracking', icon: Clock },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                  {tab.id === 'comments' && task.comments.length > 0 && (
                    <span className="ml-2 bg-gray-100 text-gray-600 text-xs rounded-full px-2 py-1">
                      {task.comments.length}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Task Overview
                </h3>
                <p className="text-gray-700">{task.description}</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Created By
                </h3>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {task.createdBy.firstName?.[0] ?? ''}
                      {task.createdBy.lastName?.[0] ?? ''}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {task.createdBy.firstName} {task.createdBy.lastName}
                    </p>
                    <p className="text-sm text-gray-600">
                      Created {getRelativeTime(task.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'comments' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Comments
                </h3>

                {/* Add Comment Form */}
                <div className="mb-6">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      disabled={!newComment.trim()}
                      onClick={handleAddComment}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      Add Comment
                    </button>
                  </div>
                </div>

                {/* Comments List */}
                <div className="space-y-4">
                  {task.comments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No comments yet</p>
                    </div>
                  ) : (
                    task.comments.map((comment) => (
                      <div key={comment._id} className="flex space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-xs">
                            {comment.user.firstName?.[0] ?? ''}
                            {comment.user.lastName?.[0] ?? ''}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-gray-900 text-sm">
                                {comment.user.firstName} {comment.user.lastName}
                              </span>
                              <span className="text-xs text-gray-500">
                                {getRelativeTime(comment.createdAt)}
                              </span>
                            </div>
                            <p className="text-gray-700 text-sm">
                              {comment.content}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'time' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Time Tracking
                </h3>

                {/* Add Time Entry Form */}
                {(isAssignedDeveloper || user?.role === 'admin') && (
                  <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Log Time</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hours
                        </label>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={newTimeEntry.hours}
                          onChange={(e) =>
                            setNewTimeEntry((prev) => ({
                              ...prev,
                              hours: parseFloat(e.target.value) || 0,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="0.0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={newTimeEntry.description}
                          onChange={(e) =>
                            setNewTimeEntry((prev) => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="What did you work on?"
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button
                        disabled={
                          !newTimeEntry.hours ||
                          !newTimeEntry.description.trim()
                        }
                        onClick={handleLogTime}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        Log Time
                      </button>
                    </div>
                  </div>
                )}

                {/* Time Entries List */}
                <div className="space-y-3">
                  {task.timeEntries.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No time entries logged</p>
                    </div>
                  ) : (
                    task.timeEntries.map((entry) => (
                      <div
                        key={entry._id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-xs">
                              {entry.user.firstName?.[0] ?? ''}
                              {entry.user.lastName?.[0] ?? ''}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">
                              {entry.user.firstName} {entry.user.lastName}
                            </p>
                            <p className="text-sm text-gray-600">
                              {entry.description}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">
                            {entry.hours}h
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(entry.date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetails;
